import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { ArrowLeft, Rocket, CheckCircle, Clock, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import ThemeToggle from '../components/ThemeToggle';

export default function FreeTrial() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [freeTest, setFreeTest] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchFreeTest = useCallback(async () => {
    try {
      const response = await api.get('/test-series/free');
      setFreeTest(response.data);
    } catch (error) {
      toast.error('Failed to load free trial test');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!user) {
      navigate('/register', { state: { returnTo: '/free-trial' } });
      return;
    }
    fetchFreeTest();
  }, [user, navigate, fetchFreeTest]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading free trial...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="free-trial-page">
      {/* Navbar */}
      <nav className="border-b border-border backdrop-blur-md bg-background/80 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/b913f950-9aae-49dc-aba5-d378a13b23f2/images/cfb7f6d255e29e51a76f73861f035a7bbb40bada25de0eb5061520d6d7d4dab1.png" 
            alt="Pinaka Logo"
            className="h-10 w-auto cursor-pointer"
            onClick={() => navigate('/')}
          />
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Button onClick={() => navigate('/dashboard')} data-testid="nav-dashboard-btn">
              Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/')} 
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-success/10 to-accent/10 border-2 border-success/20 rounded-full mb-6">
                <Rocket className="w-5 h-5 text-success" />
                <span className="text-sm font-bold text-success">100% FREE - No Payment Required!</span>
              </div>

              {freeTest.thumbnail_url && (
                <div className="relative rounded-lg overflow-hidden mb-6 border-2 border-primary/20">
                  <img src={freeTest.thumbnail_url} alt={freeTest.title} className="w-full h-80 object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent"></div>
                  <div className="absolute top-4 left-4 bg-success/90 backdrop-blur-sm px-4 py-2 rounded-full border-2 border-success">
                    <span className="text-sm font-bold text-white">FREE TRIAL</span>
                  </div>
                </div>
              )}

              <h1 className="text-4xl font-outfit font-bold mb-4">{freeTest.title}</h1>
              
              <div className="flex items-center gap-6 text-muted-foreground mb-6">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  <span>{freeTest.questions_count} Questions</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  <span>{freeTest.duration} Minutes</span>
                </div>
              </div>

              <div className="prose prose-invert max-w-none mb-6">
                <p className="text-muted-foreground leading-relaxed text-lg">{freeTest.description}</p>
              </div>

              <div className="bg-gradient-to-br from-primary/10 to-accent/10 border-2 border-primary/20 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-outfit font-semibold mb-4 flex items-center gap-2">
                  <CheckCircle className="w-6 h-6 text-success" />
                  What You'll Experience
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                    <span>Real JEE-pattern questions to assess your current level</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                    <span>Timed test environment just like the actual exam</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                    <span>Instant score report with question-by-question analysis</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                    <span>See correct answers and learn from your mistakes</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                    <span>No credit card required, completely free!</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="sticky top-24"
            >
              <div className="glass rounded-lg p-6 border-2 border-primary/20 shadow-xl">
                <div className="text-center mb-6">
                  <div className="text-5xl font-bold font-mono text-success mb-2">FREE</div>
                  <p className="text-sm text-muted-foreground">No payment required</p>
                </div>

                <Button 
                  className="w-full bg-gradient-to-r from-success to-emerald-600 hover:opacity-90 text-white font-bold shadow-lg mb-4 py-6 text-lg"
                  onClick={() => navigate(`/test/${freeTest.id}`)}
                  data-testid="start-free-test-btn"
                >
                  <Rocket className="w-5 h-5 mr-2" />
                  Start Free Test Now
                </Button>

                <div className="mt-6 pt-6 border-t-2 border-border space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Questions</span>
                    <span className="font-semibold">{freeTest.questions_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duration</span>
                    <span className="font-semibold">{freeTest.duration} minutes</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Difficulty</span>
                    <span className="font-semibold text-success">{freeTest.difficulty}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Price</span>
                    <span className="font-bold text-success text-lg">FREE</span>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-accent/10 rounded-lg border border-accent/20">
                  <p className="text-sm text-center">
                    <span className="font-semibold text-accent">💡 Pro Tip:</span> After completing this test, see how our paid test series can 10x your JEE prep!
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
